import React, { memo } from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';

import css from './SearchResultsPanel.module.css';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = memo(props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    intl,
    pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage',
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      return [
        '(max-width: 600px) calc(100vw - 48px)',
        '(max-width: 992px) calc(50vw - 40px)',
        '(max-width: 1400px) 160px',
        '143px',
      ].join(', ');
    } else {
      return [
        '(max-width: 549px) calc(100vw - 48px)',
        '(max-width: 767px) calc(50vw - 40px)',
        '(max-width: 1439px) 180px',
        '(max-width: 1920px) 160px',
        '143px',
      ].join(', ');
    }
  };

  return (
    <div className={classes}>
      <div className={css.listingWrapper}>
        <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
          {listings.map(l => (
            <div
              key={l.id.uuid}
              data-listing-id={l.id.uuid}
              data-testid={`listing-card-${l.id.uuid}`}
            >
              <ListingCard
                className={css.listingCard}
                listing={l}
                renderSizes={cardRenderSizes(isMapVariant)}
                setActiveListing={setActiveListing}
                showBiddingBadge={false}
              />
            </div>
          ))}
          {props.children}
        </div>
        {paginationLinks}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Memoize to prevent unnecessary re-renders
  return (
    prevProps.listings?.length === nextProps.listings?.length &&
    prevProps.listings?.every((listing, index) => 
      listing?.id?.uuid === nextProps.listings?.[index]?.id?.uuid
    ) &&
    prevProps.pagination?.page === nextProps.pagination?.page &&
    prevProps.pagination?.totalPages === nextProps.pagination?.totalPages &&
    prevProps.isMapVariant === nextProps.isMapVariant
  );
});

export default SearchResultsPanel;
